import React from 'react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { cn } from '../../../lib/utils';
import { Plus, Trash2 } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Editor de lista de emails de facturación.
// - emails: array de strings (controlado por el padre)
// - onChange(nextArray): callback
// - disabled: deshabilita edición (ej. modo lectura)
// - testidPrefix: prefijo para data-testid
export const EmailsFacturacionList = ({ emails, onChange, disabled = false, testidPrefix = 'facturacion' }) => {
  const list = (emails && emails.length) ? emails : [''];

  const update = (idx, val) => {
    const next = [...list];
    next[idx] = val;
    onChange(next);
  };
  const add = () => onChange([...list, '']);
  const remove = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(next.length ? next : ['']);
  };

  return (
    <div className="space-y-1.5" data-testid={`${testidPrefix}-emails-list`}>
      <Label className="text-xs">Emails para facturación</Label>
      {list.map((em, idx) => {
        const trimmed = (em || '').trim();
        const invalid = trimmed && !EMAIL_RE.test(trimmed);
        const dup = trimmed && list.findIndex((x, i) => i < idx && (x || '').trim().toLowerCase() === trimmed.toLowerCase()) >= 0;
        const hasError = invalid || dup;
        return (
          <div key={idx} className="space-y-0.5">
            <div className="flex items-center gap-2">
              <Input
                type="email"
                value={em || ''}
                onChange={(e) => update(idx, e.target.value)}
                placeholder={idx === 0 ? 'facturacion@ejemplo.com (principal)' : 'otro@ejemplo.com'}
                disabled={disabled}
                className={cn(disabled && 'bg-slate-50', hasError && 'border-red-400 focus-visible:ring-red-400')}
                data-testid={`${testidPrefix}-email-${idx}`}
              />
              {idx === 0 ? (
                <span className="text-[10px] text-slate-400 w-16 shrink-0">Principal</span>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-16 shrink-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                  disabled={disabled}
                  onClick={() => remove(idx)}
                  data-testid={`${testidPrefix}-email-remove-${idx}`}
                  title="Eliminar email"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            {hasError && (
              <p className="text-[11px] text-red-500" data-testid={`${testidPrefix}-email-error-${idx}`}>
                {invalid ? 'Formato de email inválido' : 'Email duplicado'}
              </p>
            )}
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 gap-1 mt-1"
        disabled={disabled}
        onClick={add}
        data-testid={`${testidPrefix}-email-add`}
      >
        <Plus className="w-3 h-3" /> Agregar email
      </Button>
    </div>
  );
};

// Helpers exportados para validación/normalización en los formularios.
export const cleanEmailsFacturacion = (lst) => {
  if (!lst) return [];
  const out = [];
  const seen = new Set();
  for (const e of lst) {
    const v = (e || '').trim();
    if (!v) continue;
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
};

export const emailsFacturacionTienenError = (lst) => {
  const seen = new Set();
  for (const e of (lst || [])) {
    const v = (e || '').trim();
    if (!v) continue;
    if (!EMAIL_RE.test(v)) return true;
    const k = v.toLowerCase();
    if (seen.has(k)) return true;
    seen.add(k);
  }
  return false;
};
